<template>
  <div class="h-screen flex flex-col">
    <!-- Global view-as preview banner — sits above the sidebar + main
         content so the preview context is the first thing visible -->
    <ViewAsBanner />
    <div class="flex flex-1 min-h-0">
      <!-- Side Panel -->
      <div
        class="w-64 h-screen flex flex-col bg-gray-100 dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700 transition-colors duration-200"
      >
        <div
          v-if="inIdpPortal"
          class="border-b border-gray-300 bg-transparent p-4 dark:border-gray-700"
        >
          <router-link
            to="/idp"
            class="logo-link flex items-center gap-3 rounded-lg p-1"
          >
            <span
              class="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white"
            >
              <ServerIcon class="h-5 w-5" />
            </span>
            <span class="min-w-0">
              <span class="block text-lg font-bold leading-tight text-gray-900 dark:text-white"
                >IDP</span
              >
              <span class="block truncate text-[11px] text-gray-500 dark:text-gray-400"
                >Internal Developer Platform</span
              >
            </span>
          </router-link>
        </div>
        <div
          v-else
          class="p-4 border-b border-gray-300 dark:border-gray-700 bg-transparent"
        >
          <router-link
            to="/"
            class="flex items-center justify-center cursor-pointer p-1 logo-link"
          >
            <div class="flex items-center">
              <!-- Wrench icon -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-9 w-9 mr-1"
                viewBox="0 0 24 24"
              >
                <path
                  d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"
                  fill="#FFD700"
                  stroke="#FFD700"
                  stroke-width="0.5"
                />
              </svg>

              <!-- Text logo -->
              <div class="flex items-center">
                <span class="text-3xl font-bold text-blue-500">dev</span>
                <span class="text-3xl font-bold text-green-500">tools</span>
              </div>
            </div>
          </router-link>
        </div>

        <nav
          v-if="inIdpPortal"
          class="mt-3 flex-1 overflow-y-auto pb-2"
          aria-label="IDP navigation"
        >
          <div class="flex flex-col gap-0.5">
            <router-link
              to="/idp"
              class="nav-link nav-home"
              :class="{ 'nav-active': route.path === '/idp' }"
            >
              <span class="nav-icon-wrap">
                <HomeIcon class="w-5 h-5" />
              </span>
              <span class="flex-1 min-w-0 truncate">Overview</span>
            </router-link>

            <div class="nav-heading">Explore</div>
            <template
              v-for="tool in idpTools"
              :key="tool.path"
            >
              <router-link
                :to="tool.path"
                class="nav-link"
                :class="{ 'nav-active': isIdpToolActive(tool.path) }"
              >
                <span class="nav-icon-wrap">
                  <component
                    :is="tool.icon"
                    class="w-5 h-5"
                  />
                </span>
                <span
                  class="flex-1 min-w-0 truncate"
                  :title="tool.name"
                  >{{ tool.name }}</span
                >
              </router-link>
              <router-link
                v-if="tool.path === '/idp/services'"
                :to="workspaceTo"
                class="nav-link nav-link-workspace"
                :class="{ 'nav-active': workspaceActive }"
                :title="workspaceHint"
              >
                <span class="nav-icon-wrap">
                  <FolderIcon class="h-5 w-5" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate">Workspace</span>
                  <span class="nav-hint">{{ workspaceHint }}</span>
                </span>
              </router-link>
            </template>
          </div>

          <div class="mx-3 my-4 border-t border-gray-200 dark:border-gray-700"></div>
          <router-link
            to="/"
            class="nav-link"
          >
            <span class="nav-icon-wrap">
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </span>
            <span class="flex-1 min-w-0 truncate">Developer Tools</span>
          </router-link>
        </nav>

        <nav
          v-else
          class="mt-3 flex-1 overflow-y-auto pb-2"
          aria-label="Developer Tools navigation"
        >
          <div class="flex flex-col gap-0.5">
            <!-- Home -->
            <router-link
              to="/"
              class="nav-link nav-home"
              :class="{ 'nav-active': isHome }"
            >
              <span class="nav-icon-wrap">
                <HomeIcon class="w-5 h-5" />
              </span>
              <span class="flex-1 min-w-0 truncate">Home</span>
            </router-link>

            <div class="nav-heading">Domains</div>

            <!-- Domain jump-links → launchpad sections (or a direct route for
                 full portals like IDP) -->
            <router-link
              v-for="section in sections"
              :key="section.slug"
              :to="section.directPath ?? { path: '/', hash: `#${section.slug}` }"
              class="nav-link"
              :class="{ 'nav-link-current': activeDomain === section.slug }"
            >
              <span class="nav-icon-wrap">
                <component
                  :is="section.icon"
                  class="w-5 h-5"
                />
              </span>
              <span
                class="flex-1 min-w-0 truncate"
                :title="section.name"
                >{{ section.name }}</span
              >
              <span
                v-if="activeDomain === section.slug"
                class="nav-current-dot"
                aria-hidden="true"
              />
            </router-link>
          </div>
        </nav>

        <!-- User Info and Logout -->
        <div class="shrink-0 p-4 border-t border-gray-300 dark:border-gray-700">
          <div
            v-if="user"
            class="flex items-center mb-3"
          >
            <!-- Simple colored circle with initials -->
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center mr-2"
              :style="{
                backgroundColor: getUserColor,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem'
              }"
            >
              {{ getUserInitials }}
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-800 dark:text-white">{{ user.name }}</span>
              <span class="text-xs text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{{
                user.email
              }}</span>
            </div>
          </div>

          <!-- Theme toggle -->
          <div class="flex items-center justify-between mb-3 px-1">
            <span class="text-sm text-gray-600 dark:text-gray-400">Theme</span>
            <button
              class="flex items-center justify-center rounded-full w-10 h-5 bg-gray-300 dark:bg-gray-600 relative"
              @click="theme.toggleTheme"
            >
              <!-- Sun icon for dark mode (switch to light) -->
              <svg
                v-if="theme.isDark.value"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-yellow-300 absolute right-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <!-- Moon icon for light mode (switch to dark) -->
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-blue-400 absolute left-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
              <div
                class="w-4 h-4 rounded-full absolute transition-all duration-300 transform bg-gray-200"
                :class="theme.isDark.value ? 'translate-x-2' : '-translate-x-2'"
              ></div>
            </button>
          </div>

          <button
            class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-rose-500 dark:bg-red-600 hover:bg-rose-600 dark:hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 dark:focus:ring-red-500 transition-colors duration-200"
            @click="handleLogout"
          >
            <LogoutIcon class="w-4 h-4 mr-2" />
            Logout
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex-1 overflow-auto bg-white dark:bg-gray-800">
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, inject, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FolderIcon, LogOut01Icon, Server01Icon } from '@gohighlevel/ghl-icons/24/outline'
import auth from '../../services/auth/auth'
import { useTheme } from '../../utils/useTheme'
import { isAdmin, isCostExplorerEditor } from '../../utils/adminUtils'
import { visibleIdpSection, visibleSections } from '../../config/navigation'
import { HomeIcon } from '../../config/nav-icons'
import ViewAsBanner from './ViewAsBanner.vue'
import {
  idpWorkspaceHint,
  idpWorkspaceTarget,
  rememberIdpScope,
  useIdpLastScope
} from '../../services/idp/idpLastScope'

export default defineComponent({
  name: 'SidePanel',
  components: {
    LogoutIcon: LogOut01Icon,
    ServerIcon: Server01Icon,
    FolderIcon,
    HomeIcon,
    ViewAsBanner
  },
  setup() {
    const route = useRoute()
    const theme = inject('theme', useTheme())

    // Get the logged-in user
    const user = computed(() => auth.getUser())

    const access = computed(() => ({
      isAdmin: isAdmin(user.value),
      canEditCostExplorer: isCostExplorerEditor(user.value)
    }))

    // Domain list, filtered by the same access rules the launchpad uses.
    const sections = computed(() => visibleSections(access.value))
    const idpTools = computed(() => visibleIdpSection(access.value)?.tools ?? [])
    const inIdpPortal = computed(() => route.path === '/idp' || route.path.startsWith('/idp/'))

    const lastScope = useIdpLastScope()
    const workspaceTo = computed(() => idpWorkspaceTarget(lastScope.value))
    const workspaceHint = computed(() => idpWorkspaceHint(lastScope.value))
    const workspaceActive = computed(
      () => route.path === '/idp/workspace' || route.path === '/idp/subteam'
    )

    watch(
      () => [
        route.path,
        route.query.team,
        route.query.subTeam,
        route.query.service,
        route.query.env,
        route.query.cluster
      ],
      () => {
        if (!inIdpPortal.value) return
        rememberIdpScope(route.query)
      },
      { immediate: true }
    )

    const isIdpToolActive = (target: string) => {
      const [path, queryString] = target.split('?')

      if (path === '/idp/services') {
        return route.path === '/idp/services' || route.path === '/idp/team'
      }

      if (path === '/idp/graph') {
        return route.path === '/idp/graph'
      }

      if (route.path !== path && !route.path.startsWith(`${path}/`)) return false
      if (!queryString) return true

      const expected = new URLSearchParams(queryString)
      return Array.from(expected.entries()).every(
        ([key, value]) => String(route.query[key] ?? '') === value
      )
    }

    const isHome = computed(() => route.path === '/')

    // Which domain the current route lives in — lights up its jump-link so the
    // rail still answers "where am I" even though the deep nav now lives on the
    // launchpad cards.
    const activeDomain = computed(() => {
      const path = route.path
      if (path === '/') return route.hash.replace(/^#/, '') || null
      for (const section of sections.value) {
        if (section.tools.some((t) => path === t.path || path.startsWith(`${t.path}/`))) {
          return section.slug
        }
      }
      return null
    })

    // Logout handler
    const handleLogout = () => {
      auth.logout()
    }

    const getUserInitials = computed(() => {
      if (user.value && user.value.name) {
        return user.value.name
          .split(' ')
          .map((word) => word[0])
          .join('')
          .toUpperCase()
      }
      return ''
    })

    const getUserColor = computed(() => {
      if (user.value && user.value.name) {
        const colors = ['#FF5757', '#FFB800', '#00D084', '#0083FF', '#7A5AF8']
        const index =
          user.value.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
          colors.length
        return colors[index]
      }
      return '#ccc'
    })

    return {
      sections,
      idpTools,
      inIdpPortal,
      isIdpToolActive,
      workspaceTo,
      workspaceHint,
      workspaceActive,
      isHome,
      activeDomain,
      route,
      user,
      handleLogout,
      getUserInitials,
      getUserColor,
      theme
    }
  }
})
</script>

<style scoped>
@reference "../../index.css";
/* ── Nav rows ───────────────────────────────────────────────────────────── */
.nav-link {
  @apply relative mx-2 flex items-center rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors duration-150 dark:text-gray-300;
}
.nav-link:hover {
  @apply bg-gray-200/70 text-gray-900 dark:bg-gray-800/60 dark:text-gray-100;
}
.nav-link:focus-visible {
  @apply outline-hidden ring-2 ring-inset ring-indigo-500/45;
}
.nav-home {
  @apply font-medium text-gray-700 dark:text-gray-200;
}

/* Active leaf — soft indigo wash */
.nav-active,
.nav-active:hover {
  @apply bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200;
}

/* Current domain — quiet indigo "you are here" tint */
.nav-link-current {
  @apply text-indigo-600 dark:text-indigo-300;
}

/* Section heading above the domain list */
.nav-heading {
  @apply mx-3 mb-1 mt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500;
  user-select: none;
}

/* ── Icon + current dot ─────────────────────────────────────────────────── */
.nav-icon-wrap {
  @apply relative mr-3 flex h-5 w-5 shrink-0 items-center justify-center;
}
.nav-link-workspace {
  @apply items-start;
}
.nav-hint {
  @apply mt-0.5 block truncate text-[11px] font-normal leading-4 text-gray-400 dark:text-gray-500;
}
.nav-active .nav-hint {
  @apply text-indigo-400 dark:text-indigo-300/70;
}
.nav-current-dot {
  @apply ml-1 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500 dark:bg-indigo-400;
}

/* Keep the logo unhighlighted when active */
.logo-link.router-link-active {
  background-color: transparent !important;
}
</style>
